import React, { useState, useMemo } from 'react'
import { AutoComplete, Popover, Spin } from 'antd'
import { useFFEStore } from '../../../store/ffeStore'
import { SupplierCard } from '../SupplierCard'
import { useApi } from '../../../services/ApiContext'

interface Props {
  value?: string | undefined
  onChange?: (updates: string | undefined) => void
}

export const SupplierInput: React.FC<Props> = ({ value, onChange }) => {
  const suppliers = useFFEStore((state) => state.suppliers)
  const addSupplier = useFFEStore((state) => state.addSupplier)
  const [loading, setLoading] = useState(false)
  const api = useApi()
  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier._id === value),
    [value, suppliers]
  )
  const [tempSupplier, setTempSupplier] = useState(selectedSupplier?.name)
  const [open, setOpen] = useState(false)

  const handleSelect = async (value: string) => {
    const activeSuppliers = suppliers.filter((s) => !s.isArchived)
    value = value.replace('|', '')
    if (value) {
      if (
        activeSuppliers.some(
          (s) => s.name.toLowerCase() === value.toLowerCase()
        )
      ) {
        const supplier = activeSuppliers.find(
          (s) => s.name.toLowerCase() === value.toLowerCase()
        )
        if (supplier && onChange) {
          onChange(supplier._id)
          setTempSupplier(supplier.name)
        }
      } else {
        const newSupplier = { name: value }
        setLoading(true)
        const response = await api.createSupplier(newSupplier)
        setLoading(false)
        addSupplier(response)
        if (onChange) {
          onChange(response._id)
          setTempSupplier(newSupplier.name)
        }
      }
    }
  }

  const getSupplierOptions = () => {
    const activeSuppliers = suppliers.filter((s) => !s.isArchived)
    if (
      tempSupplier &&
      !suppliers.some(
        (s) => s.name.toLowerCase() === tempSupplier.toLowerCase()
      )
    ) {
      return [
        ...activeSuppliers
          .map((s) => ({ value: s.name }))
          .filter((s) =>
            s.value.toLowerCase().includes(tempSupplier.toLowerCase())
          )
          .slice(0, 10),
        { value: '|' + tempSupplier, label: `+ Add Supplier '${tempSupplier}'` }
      ]
    }
    return activeSuppliers
      .map((s) => ({ value: s.name }))
      .filter((s) =>
        s.value.toLowerCase().includes((tempSupplier ?? '').toLowerCase())
      )
      .slice(0, 10)
  }

  if (loading) {
    return (
      <Spin spinning>
        <AutoComplete
          value={tempSupplier}
          onChange={setTempSupplier}
          onSelect={handleSelect}
          defaultActiveFirstOption
          options={getSupplierOptions()}
          placeholder="Enter supplier name"
          className="!w-[200px]"
        />
      </Spin>
    )
  }

  if (!selectedSupplier) {
    return (
      <AutoComplete
        value={tempSupplier}
        onChange={setTempSupplier}
        onSelect={handleSelect}
        defaultActiveFirstOption
        options={getSupplierOptions()}
        placeholder="Enter supplier name"
        className="!w-[200px]"
      />
    )
  }

  return (
    <Popover
      trigger="hover"
      open={open}
      onOpenChange={setOpen}
      placement="bottom"
      content={<SupplierCard supplier={selectedSupplier} />}
    >
      <AutoComplete
        value={tempSupplier}
        onChange={setTempSupplier}
        onSelect={handleSelect}
        onBlur={() => setTempSupplier(selectedSupplier.name)}
        options={getSupplierOptions()}
        placeholder="Enter supplier name"
        className="!w-[200px]"
      />
    </Popover>
  )
}
